import React, { useState, useEffect } from 'react';
import { 
  Package, DollarSign, Truck, ChevronDown, ChevronUp, Download, LogOut, Calendar,
  FileImage, Tag, Plus, Trash2, ToggleLeft, ToggleRight, Banknote, Users, 
  BarChart3, Edit2, Save, X, AlertTriangle, Filter, Search, Eye, Mail,
  ShoppingBag, TrendingUp, Clock, CheckCircle2, XCircle, FileText, Phone, Image, Grid3X3
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'one2tees2024';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderFilters, setOrderFilters] = useState({ status: '', product: '', dateFrom: '', dateTo: '' });
  const [editingNotes, setEditingNotes] = useState(null);
  const [tempNotes, setTempNotes] = useState('');
  
  // Products state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', basePrice: '', image: '' });
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProductForm, setNewProductForm] = useState({
    name: '', category: '', description: '', basePrice: '', image: ''
  });
  const [newProductLoading, setNewProductLoading] = useState(false);
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  
  // Artwork state
  const [artwork, setArtwork] = useState([]);
  const [artworkLoading, setArtworkLoading] = useState(false);
  const [artworkFilters, setArtworkFilters] = useState({ customer: '', product: '', format: '', dateFrom: '', dateTo: '' });
  
  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [newPromoDiscount, setNewPromoDiscount] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  
  // Cash order state
  const [showCashOrderForm, setShowCashOrderForm] = useState(false);
  const [cashOrderData, setCashOrderData] = useState({
    customerName: '', customerEmail: '', customerPhone: '', notes: '', promoCode: '',
    items: [{ productId: 1, productName: 'Canvas Premium', size: '24x36', quantity: 1, unitPrice: 137.49 }],
    shippingAddress: { name: '', street: '', city: 'West Palm Beach', state: 'FL', zip_code: '33405', country: 'US' }
  });
  const [cashOrderLoading, setCashOrderLoading] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('admin_auth');
    if (auth === 'true') setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboard();
      fetchOrders();
      fetchPromoCodes();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'products' && products.length === 0) fetchProducts();
    if (activeTab === 'customers' && customers.length === 0) fetchCustomers();
    if (activeTab === 'artwork' && artwork.length === 0) fetchArtwork();
  }, [activeTab]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_auth', 'true');
      setError('');
    } else {
      setError('Contraseña incorrecta');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
  };

  // ============ FETCH FUNCTIONS ============
  const fetchDashboard = async () => {
    setDashboardLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard`);
      if (response.ok) setDashboard(await response.json());
    } catch (err) { console.error('Error fetching dashboard:', err); }
    setDashboardLoading(false);
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      if (orderFilters.status) params.append('status', orderFilters.status);
      if (orderFilters.product) params.append('product', orderFilters.product);
      if (orderFilters.dateFrom) params.append('date_from', orderFilters.dateFrom);
      if (orderFilters.dateTo) params.append('date_to', orderFilters.dateTo);
      
      const response = await fetch(`${API_URL}/api/admin/orders/enhanced?${params}`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (err) { console.error('Error fetching orders:', err); }
    setOrdersLoading(false);
  };

  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (err) { console.error('Error fetching products:', err); }
    setProductsLoading(false);
  };

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/customers`);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (err) { console.error('Error fetching customers:', err); }
    setCustomersLoading(false);
  };

  const fetchArtwork = async () => {
    setArtworkLoading(true);
    try {
      const params = new URLSearchParams();
      if (artworkFilters.customer) params.append('customer', artworkFilters.customer);
      if (artworkFilters.product) params.append('product', artworkFilters.product);
      if (artworkFilters.format) params.append('file_format', artworkFilters.format);
      if (artworkFilters.dateFrom) params.append('date_from', artworkFilters.dateFrom);
      if (artworkFilters.dateTo) params.append('date_to', artworkFilters.dateTo);
      
      const response = await fetch(`${API_URL}/api/admin/artwork?${params}`);
      if (response.ok) {
        const data = await response.json();
        setArtwork(data.artwork || []);
      }
    } catch (err) { console.error('Error fetching artwork:', err); }
    setArtworkLoading(false);
  };

  const fetchPromoCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/promo-codes`);
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data.promo_codes || []);
      }
    } catch (err) { console.error('Error fetching promo codes:', err); }
  };

  // ============ ORDER FUNCTIONS ============
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/status/enhanced`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const result = await response.json();
        fetchOrders();
        fetchDashboard();
        if (result.email_sent) {
          alert('Estado actualizado y email enviado al cliente');
        }
      }
    } catch (err) { console.error('Error updating order:', err); }
  };

  const updateOrderNotes = async (orderId) => {
    try {
      await fetch(`${API_URL}/api/admin/orders/${orderId}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_notes: tempNotes })
      });
      setEditingNotes(null);
      fetchOrders();
    } catch (err) { console.error('Error updating notes:', err); }
  };

  const exportOrders = () => {
    const params = new URLSearchParams();
    if (orderFilters.status) params.append('status', orderFilters.status);
    if (orderFilters.dateFrom) params.append('date_from', orderFilters.dateFrom);
    if (orderFilters.dateTo) params.append('date_to', orderFilters.dateTo);
    window.open(`${API_URL}/api/admin/orders/export?${params}`, '_blank');
  };

  // ============ PRODUCT FUNCTIONS ============
  const startEditProduct = (product) => {
    setEditingProduct(product.id);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      basePrice: product.basePrice || '',
      image: product.image || ''
    });
  };

  const saveProduct = async (productId) => {
    try {
      await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm)
      });
      setEditingProduct(null);
      fetchProducts();
    } catch (err) { console.error('Error updating product:', err); }
  };

  const toggleProduct = async (productId) => {
    try {
      await fetch(`${API_URL}/api/admin/products/${productId}/toggle`, { method: 'PUT' });
      fetchProducts();
    } catch (err) { console.error('Error toggling product:', err); }
  };

  const createProduct = async () => {
    if (!newProductForm.name || !newProductForm.category || !newProductForm.basePrice) {
      alert('Nombre, Categoría y Precio Base son requeridos');
      return;
    }
    setNewProductLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProductForm.name,
          category: newProductForm.category,
          description: newProductForm.description || '',
          basePrice: parseFloat(newProductForm.basePrice),
          image: newProductForm.image || 'https://placehold.co/400x300?text=New+Product'
        })
      });
      if (response.ok) {
        const result = await response.json();
        alert(`¡Producto creado! ID: ${result.product.id}`);
        setNewProductForm({ name: '', category: '', description: '', basePrice: '', image: '' });
        setShowNewProductForm(false);
        fetchProducts();
      }
    } catch (err) { console.error('Error creating product:', err); alert('Error al crear producto'); }
    setNewProductLoading(false);
  };

  // ============ PROMO FUNCTIONS ============
  const createPromoCode = async () => {
    if (!newPromoCode.trim() || !newPromoDiscount) return;
    setPromoLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/promo-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newPromoCode.toUpperCase(), discount_percent: parseFloat(newPromoDiscount) })
      });
      if (response.ok) {
        setNewPromoCode('');
        setNewPromoDiscount('');
        fetchPromoCodes();
      }
    } catch (err) { console.error('Error creating promo:', err); }
    setPromoLoading(false);
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`¿Eliminar código ${code}?`)) return;
    try {
      await fetch(`${API_URL}/api/admin/promo-codes/${code}`, { method: 'DELETE' });
      fetchPromoCodes();
    } catch (err) { console.error('Error deleting promo:', err); }
  };

  const togglePromoCode = async (code) => {
    try {
      await fetch(`${API_URL}/api/admin/promo-codes/${code}/toggle`, { method: 'PUT' });
      fetchPromoCodes();
    } catch (err) { console.error('Error toggling promo:', err); }
  };

  // ============ CASH ORDER FUNCTIONS ============
  const createCashOrder = async () => {
    if (!cashOrderData.customerName || !cashOrderData.customerPhone) {
      alert('Nombre y teléfono del cliente son requeridos');
      return;
    }
    setCashOrderLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/cash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cashOrderData.items.map(item => ({
            product_id: item.productId, product_name: item.productName,
            size: item.size, quantity: item.quantity, unit_price: item.unitPrice, options: null, image_url: null
          })),
          shipping_address: { ...cashOrderData.shippingAddress, name: cashOrderData.customerName },
          customer_name: cashOrderData.customerName,
          customer_email: cashOrderData.customerEmail || null,
          customer_phone: cashOrderData.customerPhone,
          notes: cashOrderData.notes || null,
          apply_promo_code: cashOrderData.promoCode || null
        })
      });
      if (response.ok) {
        const result = await response.json();
        alert(`¡Orden creada! #${result.order_number} - Total: $${result.total.toFixed(2)}`);
        setShowCashOrderForm(false);
        setCashOrderData({
          customerName: '', customerEmail: '', customerPhone: '', notes: '', promoCode: '',
          items: [{ productId: 1, productName: 'Canvas Premium', size: '24x36', quantity: 1, unitPrice: 137.49 }],
          shippingAddress: { name: '', street: '', city: 'West Palm Beach', state: 'FL', zip_code: '33405', country: 'US' }
        });
        fetchOrders();
        fetchDashboard();
      }
    } catch (err) { console.error('Error creating order:', err); alert('Error al crear orden'); }
    setCashOrderLoading(false);
  };

  // ============ HELPERS ============
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      received: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-indigo-100 text-indigo-800',
      shipped: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      received: 'Recibido', pending: 'Pendiente', processing: 'En Producción',
      shipped: 'Enviado', completed: 'Completado', delivered: 'Entregado', cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  // ============ LOGIN SCREEN ============
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 bg-cyan-500 rounded-full flex items-center justify-center">
              <ShoppingBag className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Panel de Administración</CardTitle>
            <p className="text-slate-500">One2Tees USA</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
                data-testid="admin-password-input"
              />
              {error && <p className="text-red-500 text-sm text-center">{error}</p>}
              <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600" data-testid="admin-login-btn">
                Ingresar
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ MAIN ADMIN PANEL ============
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">One2Tees Admin</h1>
              <p className="text-xs text-slate-500">Panel de Control</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchOrders(); }}>
              Actualizar
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-7 w-full bg-white shadow-sm rounded-lg p-1">
            <TabsTrigger value="dashboard" className="flex items-center gap-1 text-xs sm:text-sm">
              <BarChart3 className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-1 text-xs sm:text-sm">
              <Package className="w-4 h-4" /> <span className="hidden sm:inline">Pedidos</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-1 text-xs sm:text-sm">
              <ShoppingBag className="w-4 h-4" /> <span className="hidden sm:inline">Productos</span>
            </TabsTrigger>
            <TabsTrigger value="artwork" className="flex items-center gap-1 text-xs sm:text-sm">
              <Image className="w-4 h-4" /> <span className="hidden sm:inline">Artwork</span>
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-1 text-xs sm:text-sm">
              <Users className="w-4 h-4" /> <span className="hidden sm:inline">Clientes</span>
            </TabsTrigger>
            <TabsTrigger value="promo" className="flex items-center gap-1 text-xs sm:text-sm">
              <Tag className="w-4 h-4" /> <span className="hidden sm:inline">Promos</span>
            </TabsTrigger>
            <TabsTrigger value="cash" className="flex items-center gap-1 text-xs sm:text-sm">
              <Banknote className="w-4 h-4" /> <span className="hidden sm:inline">Efectivo</span>
            </TabsTrigger>
          </TabsList>

          {/* ============ DASHBOARD TAB ============ */}
          <TabsContent value="dashboard">
            {dashboardLoading ? (
              <div className="text-center py-12 text-slate-500">Cargando dashboard...</div>
            ) : dashboard ? (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
                    <CardContent className="p-4">
                      <p className="text-cyan-100 text-xs">Hoy</p>
                      <p className="text-2xl font-bold">{dashboard.stats?.today?.orders || 0}</p>
                      <p className="text-cyan-100 text-sm">${(dashboard.stats?.today?.revenue || 0).toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                    <CardContent className="p-4">
                      <p className="text-emerald-100 text-xs">Esta Semana</p>
                      <p className="text-2xl font-bold">{dashboard.stats?.week?.orders || 0}</p>
                      <p className="text-emerald-100 text-sm">${(dashboard.stats?.week?.revenue || 0).toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-violet-500 to-violet-600 text-white">
                    <CardContent className="p-4">
                      <p className="text-violet-100 text-xs">Este Mes</p>
                      <p className="text-2xl font-bold">{dashboard.stats?.month?.orders || 0}</p>
                      <p className="text-violet-100 text-sm">${(dashboard.stats?.month?.revenue || 0).toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                    <CardContent className="p-4">
                      <p className="text-amber-100 text-xs">Total</p>
                      <p className="text-2xl font-bold">{dashboard.stats?.total?.orders || 0}</p>
                      <p className="text-amber-100 text-sm">${(dashboard.stats?.total?.revenue || 0).toFixed(2)}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top Products */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-500" /> Top 5 Productos
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.top_products?.length > 0 ? (
                        <div className="space-y-2">
                          {dashboard.top_products.map((product, idx) => (
                            <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                              <span className="text-sm">{product.name}</span>
                              <span className="font-bold text-cyan-600">{product.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">Sin datos</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Pending Orders */}
                  <Card className={dashboard.pending_count > 0 ? 'border-2 border-red-300' : ''}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className={`w-5 h-5 ${dashboard.pending_count > 0 ? 'text-red-500' : 'text-slate-400'}`} />
                        Pedidos Pendientes ({dashboard.pending_count || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.pending_orders?.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {dashboard.pending_orders.slice(0, 5).map((order) => (
                            <div key={order.id} className="flex justify-between items-center p-2 bg-red-50 rounded border border-red-200">
                              <div>
                                <p className="font-medium text-sm">#{order.id?.slice(-8)}</p>
                                <p className="text-xs text-slate-500">{order.customer_name}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-red-600">${order.total?.toFixed(2)}</p>
                                <Button size="sm" variant="outline" className="mt-1 h-6 text-xs"
                                  onClick={() => { setActiveTab('orders'); setExpandedOrder(order.id); }}>
                                  Ver
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No hay pedidos pendientes</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Orders */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-slate-500" /> Últimos 10 Pedidos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">ID</th>
                            <th className="text-left p-2">Cliente</th>
                            <th className="text-left p-2">Producto</th>
                            <th className="text-right p-2">Total</th>
                            <th className="text-center p-2">Estado</th>
                            <th className="text-center p-2">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard.recent_orders?.map((order) => (
                            <tr key={order.id} className="border-b hover:bg-slate-50">
                              <td className="p-2 font-mono text-xs">#{order.id?.slice(-8)}</td>
                              <td className="p-2">{order.customer_name || 'N/A'}</td>
                              <td className="p-2 max-w-32 truncate">{order.items?.[0]?.product_name || 'N/A'}</td>
                              <td className="p-2 text-right font-medium">${order.total?.toFixed(2)}</td>
                              <td className="p-2 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(order.status)}`}>
                                  {getStatusLabel(order.status)}
                                </span>
                              </td>
                              <td className="p-2 text-center">
                                <select
                                  value={order.status}
                                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                  className="text-xs border rounded px-1 py-0.5"
                                >
                                  <option value="received">Recibido</option>
                                  <option value="pending">Pendiente</option>
                                  <option value="processing">En Producción</option>
                                  <option value="shipped">Enviado</option>
                                  <option value="completed">Completado</option>
                                  <option value="delivered">Entregado</option>
                                  <option value="cancelled">Cancelado</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">Error al cargar dashboard</div>
            )}
          </TabsContent>

          {/* ============ ORDERS TAB ============ */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" /> Gestión de Pedidos
                  </CardTitle>
                  <Button onClick={exportOrders} variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" /> Exportar CSV
                  </Button>
                </div>
                {/* Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
                  <select
                    value={orderFilters.status}
                    onChange={(e) => setOrderFilters({...orderFilters, status: e.target.value})}
                    className="border rounded px-2 py-1.5 text-sm"
                  >
                    <option value="">Todos los estados</option>
                    <option value="received">Recibido</option>
                    <option value="pending">Pendiente</option>
                    <option value="processing">En Producción</option>
                    <option value="shipped">Enviado</option>
                    <option value="completed">Completado</option>
                    <option value="delivered">Entregado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                  <Input
                    placeholder="Buscar producto..."
                    value={orderFilters.product}
                    onChange={(e) => setOrderFilters({...orderFilters, product: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    type="date"
                    value={orderFilters.dateFrom}
                    onChange={(e) => setOrderFilters({...orderFilters, dateFrom: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    type="date"
                    value={orderFilters.dateTo}
                    onChange={(e) => setOrderFilters({...orderFilters, dateTo: e.target.value})}
                    className="text-sm"
                  />
                  <Button onClick={fetchOrders} size="sm" className="bg-cyan-500 hover:bg-cyan-600">
                    <Filter className="w-4 h-4 mr-1" /> Filtrar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {ordersLoading ? (
                  <div className="text-center py-12">Cargando pedidos...</div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay pedidos</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.map((order) => (
                      <div key={order.id} className={`border rounded-lg overflow-hidden ${order.artwork_issues?.length > 0 ? 'border-amber-400' : ''}`}>
                        <div
                          className="p-3 bg-slate-50 flex items-center justify-between cursor-pointer hover:bg-slate-100"
                          onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">#{order.id?.slice(-8)}</p>
                                {order.payment_method === 'cash' && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded flex items-center gap-0.5">
                                    <Banknote className="w-3 h-3" /> Efectivo
                                  </span>
                                )}
                                {order.artwork_issues?.length > 0 && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded flex items-center gap-0.5">
                                    <AlertTriangle className="w-3 h-3" /> Revisar Arte
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{formatDate(order.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm text-slate-600">{order.customer_name}</p>
                              <p className="text-xs text-slate-500">{order.customer_phone || 'Sin teléfono'}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                              {getStatusLabel(order.status)}
                            </span>
                            <p className="font-bold text-cyan-600 min-w-16 text-right">${order.total?.toFixed(2)}</p>
                            {expandedOrder === order.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </div>
                        </div>

                        {expandedOrder === order.id && (
                          <div className="p-4 border-t space-y-4">
                            {/* PDF / Invoice Download */}
                            <div className="flex justify-end">
                              <a
                                href={`${API_URL}/api/admin/orders/${order.id}/pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1A3A5C] hover:bg-[#152e4a] text-white text-sm font-medium rounded-md transition-colors"
                                data-testid={`download-order-pdf-${order.id}`}
                              >
                                <FileText className="w-4 h-4" />
                                Descargar PDF / Factura
                              </a>
                            </div>
                            {/* Customer Info */}
                            <div className="grid md:grid-cols-2 gap-4">
                              <div className="bg-slate-50 p-3 rounded">
                                <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                                  <Users className="w-4 h-4" /> Cliente
                                </h4>
                                <p className="font-medium">{order.customer_name}</p>
                                <p className="text-sm text-slate-600">{order.customer_email || 'Sin email'}</p>
                                <p className="text-sm text-slate-600 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {order.customer_phone || 'Sin teléfono'}
                                </p>
                                <div className="mt-2 text-sm">
                                  <p>{order.shipping_address?.street}</p>
                                  <p>{order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip_code}</p>
                                </div>
                              </div>

                              {/* Admin Notes */}
                              <div className="bg-amber-50 p-3 rounded border border-amber-200">
                                <h4 className="font-medium text-sm mb-2 flex items-center justify-between">
                                  <span className="flex items-center gap-1"><FileText className="w-4 h-4" /> Notas Internas</span>
                                  {editingNotes !== order.id ? (
                                    <Button size="sm" variant="ghost" onClick={() => { setEditingNotes(order.id); setTempNotes(order.admin_notes || ''); }}>
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                  ) : (
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => updateOrderNotes(order.id)}><Save className="w-3 h-3 text-green-600" /></Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingNotes(null)}><X className="w-3 h-3 text-red-600" /></Button>
                                    </div>
                                  )}
                                </h4>
                                {editingNotes === order.id ? (
                                  <textarea
                                    value={tempNotes}
                                    onChange={(e) => setTempNotes(e.target.value)}
                                    className="w-full h-20 text-sm border rounded p-2"
                                    placeholder="Añadir notas internas..."
                                  />
                                ) : (
                                  <p className="text-sm text-slate-600 italic">{order.admin_notes || 'Sin notas'}</p>
                                )}
                              </div>
                            </div>

                            {/* Products */}
                            <div>
                              <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                                <ShoppingBag className="w-4 h-4" /> Productos
                              </h4>
                              <div className="space-y-2">
                                {order.items?.map((item, idx) => (
                                  <div key={idx} className="bg-slate-50 p-3 rounded">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <p className="font-medium">{item.product_name}</p>
                                        <p className="text-xs text-slate-500">
                                          Tamaño: {item.size} • Cantidad: {item.quantity}
                                        </p>
                                        {/* Options */}
                                        {item.options && Object.keys(item.options).length > 0 && (
                                          <div className="mt-1 flex flex-wrap gap-1">
                                            {Object.entries(item.options).filter(([k, v]) => v && v !== 'No' && v !== 'None').map(([key, value]) => (
                                              <span key={key} className="px-1.5 py-0.5 bg-slate-200 text-xs rounded">{key}: {value}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <p className="font-medium">${(item.unit_price * item.quantity).toFixed(2)}</p>
                                    </div>
                                    {/* Artwork File */}
                                    {item.uploaded_file && (
                                      <div className="mt-2 flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                                        <FileImage className="w-4 h-4 text-green-600" />
                                        <span className="text-xs text-green-700 flex-1 truncate">{item.uploaded_file.filename}</span>
                                        <a
                                          href={`${API_URL}/api/files/${item.uploaded_file.file_id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 bg-green-100 rounded hover:bg-green-200"
                                        >
                                          <Download className="w-4 h-4 text-green-600" />
                                        </a>
                                        <a
                                          href={`${API_URL}/api/files/${item.uploaded_file.file_id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 bg-blue-100 rounded hover:bg-blue-200"
                                        >
                                          <Eye className="w-4 h-4 text-blue-600" />
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Artwork Issues */}
                            {order.artwork_issues?.length > 0 && (
                              <div className="bg-amber-50 border border-amber-300 p-3 rounded">
                                <h4 className="font-medium text-sm text-amber-800 flex items-center gap-1 mb-2">
                                  <AlertTriangle className="w-4 h-4" /> Posibles Problemas con Archivos
                                </h4>
                                <ul className="text-sm text-amber-700 list-disc list-inside">
                                  {order.artwork_issues.map((issue, idx) => (
                                    <li key={idx}>{issue}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Terms & Conditions Acceptance */}
                            <div className={`p-3 rounded border ${order.terms_consent?.terms_accepted ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                              <h4 className="font-medium text-sm flex items-center gap-2 mb-2">
                                <FileText className="w-4 h-4" /> 
                                Términos y Condiciones
                              </h4>
                              {order.terms_consent?.terms_accepted ? (
                                <div className="text-sm">
                                  <p className="text-green-700 font-medium flex items-center gap-1">
                                    <CheckCircle2 className="w-4 h-4" /> T&C Aceptados: ✓ SÍ
                                  </p>
                                  <p className="text-slate-600 text-xs mt-1">
                                    <strong>Fecha:</strong> {order.terms_consent.terms_accepted_at?.replace('T', ' ').slice(0, 19) || 'N/A'}
                                  </p>
                                  <p className="text-slate-600 text-xs">
                                    <strong>IP:</strong> {order.terms_consent.ip_address || 'N/A'}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-red-700 text-sm flex items-center gap-1">
                                  <XCircle className="w-4 h-4" /> Sin registro de aceptación
                                </p>
                              )}
                            </div>

                            {/* Totals */}
                            <div className="bg-slate-50 p-3 rounded">
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <span>Subtotal:</span><span className="text-right">${order.subtotal?.toFixed(2)}</span>
                                {order.discount_amount > 0 && (
                                  <>
                                    <span className="text-green-600">Descuento ({order.promo_code}):</span>
                                    <span className="text-right text-green-600">-${order.discount_amount?.toFixed(2)}</span>
                                  </>
                                )}
                                <span>Envío:</span><span className="text-right">{order.shipping_cost === 0 ? 'GRATIS' : `$${order.shipping_cost?.toFixed(2)}`}</span>
                                <span>Handling Fee (3.5%):</span><span className="text-right">${order.handling_fee?.toFixed(2) || '0.00'}</span>
                                <span>Impuesto (7%):</span><span className="text-right">${order.sales_tax?.toFixed(2)}</span>
                                <span className="font-bold pt-2 border-t">Total:</span>
                                <span className="font-bold text-cyan-600 text-right pt-2 border-t">${order.total?.toFixed(2)}</span>
                              </div>
                            </div>

                            {/* Status Update */}
                            <div className="flex items-center gap-3 pt-2 border-t">
                              <span className="text-sm text-slate-600">Cambiar estado:</span>
                              <select
                                value={order.status}
                                onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                className="border rounded px-2 py-1 text-sm"
                              >
                                <option value="received">Recibido</option>
                                <option value="pending">Pendiente</option>
                                <option value="processing">En Producción</option>
                                <option value="shipped">Enviado</option>
                                <option value="completed">Completado</option>
                                <option value="delivered">Entregado</option>
                                <option value="cancelled">Cancelado</option>
                              </select>
                              <span className="text-xs text-slate-500">
                                <Mail className="w-3 h-3 inline mr-1" />
                                Se enviará email al cambiar a Producción/Enviado/Completado/Entregado
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ PRODUCTS TAB ============ */}
          {/* ============ PRODUCTS TAB ============ */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5" /> Gestión de Productos
                  </CardTitle>
                  <Button onClick={() => setShowNewProductForm(!showNewProductForm)} className="bg-emerald-500 hover:bg-emerald-600">
                    <Plus className="w-4 h-4 mr-1" /> Nuevo Producto
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* New Product Form */}
                {showNewProductForm && (
                  <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-4">Crear Nuevo Producto</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nombre *</Label>
                        <Input value={newProductForm.name} onChange={(e) => setNewProductForm({...newProductForm, name: e.target.value})} placeholder="Ej: Custom Posters" />
                      </div>
                      <div>
                        <Label>Categoría *</Label>
                        <Input value={newProductForm.category} onChange={(e) => setNewProductForm({...newProductForm, category: e.target.value})} placeholder="Ej: posters" />
                      </div>
                      <div>
                        <Label>Precio Base * ($)</Label>
                        <Input type="number" step="0.01" value={newProductForm.basePrice} onChange={(e) => setNewProductForm({...newProductForm, basePrice: e.target.value})} placeholder="99.99" />
                      </div>
                      <div>
                        <Label>URL de Imagen</Label>
                        <Input value={newProductForm.image} onChange={(e) => setNewProductForm({...newProductForm, image: e.target.value})} placeholder="https://..." />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Descripción</Label>
                        <Input value={newProductForm.description} onChange={(e) => setNewProductForm({...newProductForm, description: e.target.value})} placeholder="Descripción del producto..." />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={createProduct} disabled={newProductLoading} className="bg-emerald-600">
                        {newProductLoading ? 'Creando...' : 'Crear Producto'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowNewProductForm(false)}>Cancelar</Button>
                    </div>
                  </div>
                )}

                {productsLoading ? (
                  <div className="text-center py-12">Cargando productos...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3">ID</th>
                          <th className="text-left p-3">Nombre</th>
                          <th className="text-left p-3">Categoría</th>
                          <th className="text-right p-3">Precio Base</th>
                          <th className="text-center p-3">Estado</th>
                          <th className="text-center p-3">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => (
                          <tr key={product.id} className={`border-b hover:bg-slate-50 ${product.active === false ? 'opacity-50' : ''}`}>
                            <td className="p-3 font-mono">{product.id}</td>
                            <td className="p-3">
                              {editingProduct === product.id ? (
                                <Input
                                  value={productForm.name}
                                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                                  className="h-8 text-sm"
                                />
                              ) : (
                                <div className="flex items-center gap-2">
                                  <img src={product.image} alt="" className="w-10 h-10 object-cover rounded" />
                                  <span className="font-medium">{product.name}</span>
                                </div>
                              )}
                            </td>
                            <td className="p-3">
                              <span className="px-2 py-0.5 bg-slate-100 rounded text-xs">{product.category}</span>
                            </td>
                            <td className="p-3 text-right">
                              {editingProduct === product.id ? (
                                <Input
                                  type="number"
                                  value={productForm.basePrice}
                                  onChange={(e) => setProductForm({...productForm, basePrice: e.target.value})}
                                  className="h-8 text-sm w-24 ml-auto"
                                />
                              ) : (
                                <span>${product.basePrice || product.pricePerSqFt || '—'}</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs ${product.active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {product.active === false ? 'Inactivo' : 'Activo'}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {editingProduct === product.id ? (
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => saveProduct(product.id)}>
                                      <Save className="w-4 h-4 text-green-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingProduct(null)}>
                                      <X className="w-4 h-4 text-red-600" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => startEditProduct(product)}>
                                      <Edit2 className="w-4 h-4 text-slate-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => toggleProduct(product.id)}>
                                      {product.active === false ? (
                                        <ToggleLeft className="w-4 h-4 text-slate-400" />
                                      ) : (
                                        <ToggleRight className="w-4 h-4 text-green-600" />
                                      )}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ ARTWORK TAB ============ */}
          <TabsContent value="artwork">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" /> Archivo de Arte
                </CardTitle>
                {/* Filters */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-4">
                  <Input
                    placeholder="Cliente..."
                    value={artworkFilters.customer}
                    onChange={(e) => setArtworkFilters({...artworkFilters, customer: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Producto..."
                    value={artworkFilters.product}
                    onChange={(e) => setArtworkFilters({...artworkFilters, product: e.target.value})}
                    className="text-sm"
                  />
                  <select
                    value={artworkFilters.format}
                    onChange={(e) => setArtworkFilters({...artworkFilters, format: e.target.value})}
                    className="border rounded px-2 py-1.5 text-sm"
                  >
                    <option value="">Todos los formatos</option>
                    <option value="pdf">PDF</option>
                    <option value="png">PNG</option>
                    <option value="jpg">JPG</option>
                    <option value="ai">AI</option>
                    <option value="psd">PSD</option>
                  </select>
                  <Input
                    type="date"
                    value={artworkFilters.dateFrom}
                    onChange={(e) => setArtworkFilters({...artworkFilters, dateFrom: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    type="date"
                    value={artworkFilters.dateTo}
                    onChange={(e) => setArtworkFilters({...artworkFilters, dateTo: e.target.value})}
                    className="text-sm"
                  />
                  <Button onClick={fetchArtwork} size="sm" className="bg-cyan-500 hover:bg-cyan-600">
                    <Filter className="w-4 h-4 mr-1" /> Filtrar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {artworkLoading ? (
                  <div className="text-center py-12">Cargando archivos...</div>
                ) : artwork.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay archivos de arte</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {artwork.map((file, idx) => (
                      <div key={idx} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                        {/* Thumbnail */}
                        <div className="h-32 bg-slate-100 flex items-center justify-center relative">
                          {file.content_type?.startsWith('image/') ? (
                            <img 
                              src={`${API_URL}/api/files/${file.file_id}`}
                              alt={file.filename}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div className={`absolute inset-0 flex items-center justify-center bg-slate-200 ${file.content_type?.startsWith('image/') ? 'hidden' : ''}`}>
                            <FileImage className="w-12 h-12 text-slate-400" />
                          </div>
                          <span className="absolute top-2 right-2 px-2 py-0.5 bg-slate-800 text-white text-xs rounded">
                            {file.file_format}
                          </span>
                        </div>
                        {/* Info */}
                        <div className="p-3">
                          <p className="font-medium text-sm truncate" title={file.filename}>{file.filename}</p>
                          <p className="text-xs text-slate-500">{file.customer_name}</p>
                          <p className="text-xs text-slate-400 truncate">{file.product_name}</p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-slate-400">#{file.order_number}</span>
                            <span className="text-xs text-slate-400">{file.upload_date}</span>
                          </div>
                          <a
                            href={`${API_URL}/api/files/${file.file_id}`}
                            download={file.filename}
                            className="mt-2 w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-cyan-500 text-white text-xs rounded hover:bg-cyan-600"
                          >
                            <Download className="w-3 h-3" /> Descargar
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {artwork.length > 0 && (
                  <p className="text-center text-slate-500 text-sm mt-4">
                    Mostrando {artwork.length} archivos
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ CUSTOMERS TAB ============ */}
          <TabsContent value="customers">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" /> Base de Clientes
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => window.open(`${API_URL}/api/admin/customers/export`, '_blank')}>
                    <Download className="w-4 h-4 mr-2" /> Exportar CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {customersLoading ? (
                  <div className="text-center py-12">Cargando clientes...</div>
                ) : customers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay clientes</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3">Cliente</th>
                          <th className="text-left p-3">Contacto</th>
                          <th className="text-center p-3">Pedidos</th>
                          <th className="text-right p-3">Total Gastado</th>
                          <th className="text-left p-3">Último Pedido</th>
                          <th className="text-center p-3">Archivos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customers.map((customer, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                                  <span className="text-cyan-700 font-medium">{customer.name?.charAt(0) || '?'}</span>
                                </div>
                                <div>
                                  <p className="font-medium">{customer.name || 'Sin nombre'}</p>
                                  {customer.registered && <span className="text-xs text-green-600">Registrado</span>}
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <p className="text-sm">{customer.email}</p>
                              <p className="text-xs text-slate-500">{customer.phone || 'Sin teléfono'}</p>
                            </td>
                            <td className="p-3 text-center">
                              <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded font-medium">{customer.order_count}</span>
                            </td>
                            <td className="p-3 text-right font-medium text-emerald-600">${customer.total_spent?.toFixed(2)}</td>
                            <td className="p-3 text-sm text-slate-600">{customer.last_order?.slice(0, 10) || 'N/A'}</td>
                            <td className="p-3 text-center">
                              {customer.files?.length > 0 ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">{customer.files.length} archivos</span>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ PROMO CODES TAB ============ */}
          <TabsContent value="promo">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5" /> Códigos Promocionales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Create new promo */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-3">Crear Nuevo Código</h4>
                  <div className="flex gap-3">
                    <Input
                      placeholder="CODIGO"
                      value={newPromoCode}
                      onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="% Desc."
                      value={newPromoDiscount}
                      onChange={(e) => setNewPromoDiscount(e.target.value)}
                      className="w-24"
                    />
                    <Button onClick={createPromoCode} disabled={promoLoading || !newPromoCode || !newPromoDiscount} className="bg-cyan-500">
                      <Plus className="w-4 h-4 mr-1" /> Crear
                    </Button>
                  </div>
                </div>
                {/* List */}
                <div className="space-y-2">
                  {promoCodes.map((promo) => (
                    <div key={promo.code} className={`flex items-center justify-between p-4 border rounded-lg ${promo.active ? 'bg-white' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3">
                        <Tag className={`w-5 h-5 ${promo.active ? 'text-green-600' : 'text-slate-400'}`} />
                        <div>
                          <p className={`font-bold ${promo.active ? '' : 'text-slate-400'}`}>{promo.code}</p>
                          <p className="text-sm text-slate-500">{promo.discount_percent}% • {promo.uses_count || 0} usos</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => togglePromoCode(promo.code)}>
                          {promo.active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePromoCode(promo.code)} className="text-red-500">
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ CASH ORDERS TAB ============ */}
          <TabsContent value="cash">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="w-5 h-5" /> Órdenes en Efectivo
                  </CardTitle>
                  <Button onClick={() => setShowCashOrderForm(!showCashOrderForm)} className="bg-amber-500 hover:bg-amber-600">
                    <Plus className="w-4 h-4 mr-1" /> Nueva Orden
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showCashOrderForm && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6 space-y-4">
                    <h4 className="font-bold text-amber-800">Crear Orden en Efectivo</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nombre *</Label>
                        <Input value={cashOrderData.customerName} onChange={(e) => setCashOrderData({...cashOrderData, customerName: e.target.value})} />
                      </div>
                      <div>
                        <Label>Teléfono * <span className="text-red-500">(Requerido)</span></Label>
                        <Input value={cashOrderData.customerPhone} onChange={(e) => setCashOrderData({...cashOrderData, customerPhone: e.target.value})} />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={cashOrderData.customerEmail} onChange={(e) => setCashOrderData({...cashOrderData, customerEmail: e.target.value})} />
                      </div>
                      <div>
                        <Label>Código Promo</Label>
                        <Input value={cashOrderData.promoCode} onChange={(e) => setCashOrderData({...cashOrderData, promoCode: e.target.value.toUpperCase()})} />
                      </div>
                    </div>
                    <div>
                      <Label>Dirección</Label>
                      <Input value={cashOrderData.shippingAddress.street} onChange={(e) => setCashOrderData({...cashOrderData, shippingAddress: {...cashOrderData.shippingAddress, street: e.target.value}})} />
                    </div>
                    <div className="border-t pt-4">
                      <h5 className="font-medium mb-2">Producto</h5>
                      <div className="grid grid-cols-4 gap-2">
                        <Input placeholder="Producto" value={cashOrderData.items[0].productName} onChange={(e) => { const items = [...cashOrderData.items]; items[0].productName = e.target.value; setCashOrderData({...cashOrderData, items}); }} />
                        <Input placeholder="Tamaño" value={cashOrderData.items[0].size} onChange={(e) => { const items = [...cashOrderData.items]; items[0].size = e.target.value; setCashOrderData({...cashOrderData, items}); }} />
                        <Input type="number" placeholder="Cant." value={cashOrderData.items[0].quantity} onChange={(e) => { const items = [...cashOrderData.items]; items[0].quantity = parseInt(e.target.value) || 1; setCashOrderData({...cashOrderData, items}); }} />
                        <Input type="number" placeholder="Precio" step="0.01" value={cashOrderData.items[0].unitPrice} onChange={(e) => { const items = [...cashOrderData.items]; items[0].unitPrice = parseFloat(e.target.value) || 0; setCashOrderData({...cashOrderData, items}); }} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={createCashOrder} disabled={cashOrderLoading || !cashOrderData.customerName || !cashOrderData.customerPhone} className="bg-amber-600">
                        {cashOrderLoading ? 'Creando...' : 'Crear Orden'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowCashOrderForm(false)}>Cancelar</Button>
                    </div>
                  </div>
                )}
                {/* Cash orders list */}
                <div className="space-y-3">
                  {orders.filter(o => o.payment_method === 'cash').length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No hay órdenes en efectivo</p>
                  ) : (
                    orders.filter(o => o.payment_method === 'cash').map((order) => (
                      <div key={order.id} className="p-4 border border-amber-200 bg-amber-50 rounded-lg flex justify-between items-start">
                        <div>
                          <p className="font-medium">#{order.id?.slice(-8)} - {order.customer_name}</p>
                          <p className="text-sm text-slate-600">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-amber-700">${order.total?.toFixed(2)}</p>
                          <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(order.status)}`}>{getStatusLabel(order.status)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPanel;
